<template>
  <div class="channel-graph">
    <div v-if="showHeader" class="d-flex align-center px-4 py-2 graph-header">
      <v-icon class="mr-2">mdi-graph-outline</v-icon>
      <span class="text-subtitle-1">{{ headerTitle }}</span>
      <v-chip v-if="graph.truncated" size="small" color="warning" variant="tonal" class="ml-3">показаны самые связанные</v-chip>
    </div>
    <div>
    <div class="graph-layout d-flex" style="height: var(--graph-height, 72vh);">
      <!-- Graph -->
      <div class="graph-main d-flex flex-column flex-grow-1">
        <div class="controls px-4 py-2">
        <div class="d-flex align-center flex-wrap ga-3">
          <v-btn-toggle v-if="!isNetworkMode && !isAmongMode" v-model="depth" mandatory density="compact" variant="outlined" divided>
            <v-btn :value="1" size="small">Соседи</v-btn>
            <v-btn :value="2" size="small">Соседи соседей</v-btn>
          </v-btn-toggle>
          <v-chip-group v-model="types" multiple column>
            <v-chip v-for="t in edgeTypes" :key="t.value" :value="t.value" size="small" filter variant="tonal" :color="t.color">{{ t.label }}</v-chip>
          </v-chip-group>
          <div class="d-flex align-center" style="min-width: 220px;">
            <v-tooltip text="Показывать связь, только если по ней прошло не меньше стольких постов (репостов / ссылок / одинаковых постов)" location="bottom">
              <template #activator="{ props }">
                <span v-bind="props" class="text-caption mr-2">Мин. постов в связи</span>
              </template>
            </v-tooltip>
            <!-- log scale: small values get most of the track; the field takes an exact number -->
            <v-slider
              v-model="minWeightPos"
              :min="0"
              :max="100"
              :step="1"
              density="compact"
              hide-details
              style="width: 140px;"
            />
            <v-text-field
              v-model.number="minWeightDraft"
              type="number"
              :min="1"
              :max="MAX_WEIGHT"
              density="compact"
              variant="outlined"
              hide-details
              class="ml-2"
              style="max-width: 84px;"
              @update:model-value="applyMinWeightLater"
            />
          </div>
        </div>
        <div class="d-flex align-center flex-wrap ga-3 mt-2">
          <v-select v-model="periodDays" :items="periodOptions" density="compact" variant="outlined" hide-details style="max-width: 150px;" />
          <v-tooltip text="Анимация force-раскладки. Для графов от 150 узлов выключена по умолчанию: раскладка считается сразу, узлы не «кружат»" location="bottom">
            <template #activator="{ props }">
              <v-switch v-bind="props" v-model="layoutAnimation" density="compact" hide-details inset color="primary" label="Анимация" class="ml-1" />
            </template>
          </v-tooltip>
          <v-btn size="small" variant="text" icon="mdi-refresh" :loading="loading" @click="load" />
          <v-btn variant="text" title="Масштаб +" class="zoom-btn" @click="zoomBy(1.25)"><v-icon size="30">mdi-magnify-plus-outline</v-icon></v-btn>
          <v-btn variant="text" title="Масштаб −" class="zoom-btn" @click="zoomBy(0.8)"><v-icon size="30">mdi-magnify-minus-outline</v-icon></v-btn>
          <v-chip v-if="hiddenIsolatedCount" size="small" variant="tonal" color="grey" prepend-icon="mdi-eye-off-outline" title="Каналы без связей по текущим фильтрам не рисуются">
            скрыто без связей: {{ hiddenIsolatedCount }}
          </v-chip>
        </div>
        </div>
        <div class="flex-grow-1 position-relative">
          <v-progress-linear v-if="loading" indeterminate color="primary" class="position-absolute" style="top:0; z-index:1;" />
          <v-chart v-if="visibleNodes.length && graph.edges.length" ref="chart" :option="chartOption" :update-options="{ notMerge: false, lazyUpdate: true }" autoresize class="graph-chart" @click="onChartClick" />
          <div v-else-if="!loading" class="d-flex align-center justify-center fill-height text-grey">
            Связей по выбранным фильтрам нет
          </div>
        </div>
      </div>

      <!-- Splitter -->
      <div v-if="!hideSidePanel" class="splitter" title="Потяните, чтобы изменить ширину панели" @mousedown.prevent="startResize"></div>

      <!-- Side panel -->
      <div v-if="!hideSidePanel" class="side-panel" :style="{ width: sidePanelWidth + 'px', minWidth: '240px' }">
        <div class="pa-3 d-flex align-center">
          <v-avatar size="36" class="mr-2">
            <v-img v-if="panelNode && avatarSrc(panelNode)" :src="avatarSrc(panelNode)" cover />
            <v-icon v-else>mdi-account-circle</v-icon>
          </v-avatar>
          <div class="flex-grow-1 overflow-hidden">
            <div
              class="font-weight-medium text-truncate"
              :class="{ 'panel-name-link': canRecenter(panelNode) }"
              :title="canRecenter(panelNode) ? 'Открыть граф этого канала' : ''"
              @click="canRecenter(panelNode) && recenter(panelNode)"
            >{{ panelNode?.title || ('@' + (panelNode?.username || '')) }}</div>
            <div class="text-caption text-grey">
              <v-chip v-if="panelNode?.is_aggregator" size="x-small" color="deep-orange" variant="tonal" class="mr-1">агрегатор</v-chip>
              @{{ panelNode?.username }}
              <span v-if="panelNode?.subscribers"> · {{ formatNumber(panelNode.subscribers) }} подп.</span>
              <span v-if="panelNode && !panelNode.source_id" class="text-warning"> · нет в базе</span>
            </div>
          </div>
          <v-tooltip v-if="canRecenter(panelNode)" text="Открыть граф этого канала">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="mdi-crosshairs-gps" size="small" variant="text" @click="recenter(panelNode)" />
            </template>
          </v-tooltip>
          <v-tooltip v-if="panelNode && isAdmin" text="Открыть в каталоге ТГ-каналов (новая вкладка)">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="mdi-open-in-new" size="small" variant="text" @click.stop="openInCatalog(panelNode)" />
            </template>
          </v-tooltip>
          <v-tooltip v-if="panelNode" :text="panelNode.in_my_sources ? 'Уже в моих источниках' : (isAdmin ? 'Добавить в мои источники' : 'Добавить в мои источники (включит мониторинг)')">
            <template #activator="{ props }">
              <span v-bind="props"><v-btn icon="mdi-plus-circle-outline" size="small" variant="text" color="primary" :disabled="!panelNode.username || panelNode.in_my_sources" @click.stop="addToMySources(panelNode)" /></span>
            </template>
          </v-tooltip>
          <v-tooltip v-if="panelNode && isAdmin && panelNode.source_id" :text="panelNode.is_aggregator ? 'Снять флаг «агрегатор»' : 'Пометить как агрегатор (исключить из кластеризации сетей)'">
            <template #activator="{ props }">
              <v-btn v-bind="props" :icon="panelNode.is_aggregator ? 'mdi-layers-off-outline' : 'mdi-layers-outline'" size="small" variant="text" :color="panelNode.is_aggregator ? 'deep-orange' : undefined" @click.stop="toggleAggregator(panelNode)" />
            </template>
          </v-tooltip>
          <v-tooltip v-if="panelNode && isAdmin && panelNode.source_id" :text="panelNode.is_monitoring ? 'Уже на мониторинге' : 'Поставить на мониторинг'">
            <template #activator="{ props }">
              <span v-bind="props"><v-btn icon="mdi-play-circle-outline" size="small" variant="text" color="success" :disabled="panelNode.is_monitoring" @click.stop="startMonitoring(panelNode)" /></span>
            </template>
          </v-tooltip>
        </div>
        <v-divider />
        <v-data-table
          :headers="edgeHeaders"
          :items="edgeRows"
          :loading="edgesLoading"
          density="compact"
          hover
          :items-per-page="-1"
          hide-default-footer
          class="edges-table"
          :height="tableHeight"
          fixed-header
          @click:row="(_, { item }) => openExamples(item)"
        >
          <template #[`item.direction`]="{ item }">
            <v-icon size="small" :color="item.direction === 'out' ? 'primary' : 'secondary'">
              {{ item.direction === 'out' ? 'mdi-arrow-right' : 'mdi-arrow-left' }}
            </v-icon>
          </template>
          <template #[`item.username`]="{ item }">
            <div class="font-weight-medium text-truncate" style="max-width: 170px;">{{ item.title || ('@' + item.username) }}</div>
            <div class="text-caption text-grey"><span v-if="item.is_aggregator" class="text-deep-orange">агрегатор · </span>@{{ item.username }}</div>
          </template>
          <template #[`item.actions`]="{ item }">
            <div class="d-flex align-center row-actions">
              <v-btn icon="mdi-message-text-outline" size="x-small" variant="text" title="Примеры постов" @click.stop="openExamples(item)" />
              <v-btn v-if="isAdmin" icon="mdi-open-in-new" size="x-small" variant="text" title="Открыть в каталоге ТГ-каналов" @click.stop="openInCatalog(item)" />
              <v-btn icon="mdi-plus-circle-outline" size="x-small" variant="text" color="primary" :title="item.in_my_sources ? 'Уже в моих источниках' : (isAdmin ? 'Добавить в мои источники' : 'Добавить в мои источники (включит мониторинг)')" :disabled="!item.username || item.in_my_sources" @click.stop="addToMySources(item)" />
              <v-btn v-if="isAdmin" icon="mdi-play-circle-outline" size="x-small" variant="text" color="success" :title="item.is_monitoring ? 'Уже на мониторинге' : 'Поставить на мониторинг'" :disabled="!item.source_id || item.is_monitoring" @click.stop="startMonitoring(item)" />
            </div>
          </template>
          <template #[`item.edge_type`]="{ item }">
            <v-chip size="x-small" variant="tonal" :color="typeMeta(item.edge_type).color">{{ typeMeta(item.edge_type).label }}</v-chip>
          </template>
          <template #[`item.last_seen_at`]="{ item }">
            <span class="text-caption">{{ formatDate(item.last_seen_at) }}</span>
          </template>
          <template #no-data>
            <div class="text-center text-caption text-grey pa-4">Нет связей по выбранным фильтрам</div>
          </template>
        </v-data-table>
        <div v-if="edgesTotal > edgeRows.length" class="text-center pa-2">
          <v-btn size="small" variant="text" :loading="edgesLoading" @click="loadEdges(true)">Показать ещё ({{ edgesTotal - edgeRows.length }})</v-btn>
        </div>
      </div>
    </div>
  </div>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" location="bottom">{{ snackbar.text }}</v-snackbar>
    <SourceContentModal
  v-model="showExamples"
  :source-id="examples.sourceId"
  :title="examples.title"
  :mention="examples.mention"
  :edge-type="examples.edgeType"
  items-label="постов"
  empty-label="Посты с этой связью не найдены"
/>
  </div>
</template>

<script>
import { base64Src, isStoredAvatar } from '@/utils/avatar';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { GraphChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import VChart from 'vue-echarts';
import { mapGetters } from 'vuex';
import apiClient from '@/apiClient';
import SourceContentModal from '@/components/SourceContentModal.vue';

// Настройки графа (типы связей, мин. постов, период, глубина, анимация) живут в cookie —
// тот же подход, что у дашборда (dashboard_selected_project).
const SETTINGS_COOKIE = 'tg_graph_settings';
function readSettingsCookie() {
  try {
    const raw = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(SETTINGS_COOKIE + '='));
    return raw ? JSON.parse(decodeURIComponent(raw.slice(SETTINGS_COOKIE.length + 1))) : null;
  } catch (e) { return null; }
}
function writeSettingsCookie(obj) {
  try {
    document.cookie = `${SETTINGS_COOKIE}=${encodeURIComponent(JSON.stringify(obj))};path=/;max-age=31536000`;
  } catch (e) { /* cookies недоступны — работаем без сохранения */ }
}

use([CanvasRenderer, GraphChart, TooltipComponent, LegendComponent]);

// Дефолтная аватарка (как mdi-account-circle серым) для узлов без картинки —
// data-URI, чтобы echarts мог использовать её как image-symbol.
const DEFAULT_AVATAR = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="#e0e0e0"/>'
  + '<circle cx="12" cy="9" r="4" fill="#9e9e9e"/><path d="M4 20c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5v1H4z" fill="#9e9e9e"/></svg>'
);

const EDGE_TYPES = [
  { value: 'forward', label: 'Репосты', color: 'primary', hex: '#1976d2' },
  { value: 'link', label: 'Ссылки', color: 'success', hex: '#43a047' },
  { value: 'coposting', label: 'Одинаковые посты', color: 'warning', hex: '#fb8c00' },
  { value: 'reply', label: 'Ответы', color: 'grey', hex: '#9e9e9e' },
];

export default {
  name: 'ChannelGraph',
  components: { VChart, SourceContentModal },
  props: {
    // Режим «эго-граф канала»: sourceId (+ username для заголовка до загрузки)
    sourceId: { type: [Number, String], default: null },
    username: { type: String, default: '' },
    // Режим «сетка»: networkId — узлы = участники, рёбра между ними, hub = центр
    networkId: { type: [Number, String], default: null },
    // Режим «граф между заданными каналами» (виджет «Распространение» проекта):
    // sourceIds — узлы, nodeValues — {source_id: число} для размера узла (напр. находок)
    sourceIds: { type: Array, default: null },
    nodeValues: { type: Object, default: null },
    showHeader: { type: Boolean, default: false },
    hideSidePanel: { type: Boolean, default: false },
    // Канал, который выделить после загрузки (поиск по каналу на странице сетей)
    highlightUsername: { type: String, default: '' },
    height: { type: String, default: '72vh' },
  },
  emits: ['recenter'],
  data() {
    return {
      edgeTypes: EDGE_TYPES,
      depth: 1,
      types: ['forward', 'link', 'coposting'],
      minWeight: 1,
      minWeightDraft: 1,      // the number field and the slider, applied after a pause
      minWeightTimer: null,
      MAX_WEIGHT: 500,
      periodDays: 0, // 0 = за всё время
      periodOptions: [
        { title: 'За всё время', value: 0 }, { title: '30 дней', value: 30 },
        { title: '90 дней', value: 90 }, { title: '180 дней', value: 180 }, { title: 'Год', value: 365 },
      ],
      loading: false,
      graph: { nodes: [], edges: [], truncated: false },
      centerSourceId: null,
      centerUsername: '',
      selectedUsername: null,
      edgeRows: [],
      edgesTotal: 0,
      edgesPage: 1,
      edgesLoading: false,
      networkName: '',
      brokenImages: {},   // url → true, если картинка не загрузилась (fallback на дефолт)
      showExamples: false,
      examples: { sourceId: null, title: '', mention: '', edgeType: '' },
      edgeHeaders: [
        { title: '', key: 'direction', width: 36, sortable: false },
        { title: 'Канал', key: 'username' },
        { title: 'Тип', key: 'edge_type', width: 110 },
        { title: 'Постов', key: 'weight', align: 'end', width: 80 },
        { title: 'Последний', key: 'last_seen_at', width: 100 },
        { title: 'Действия', key: 'actions', width: 130, sortable: false },
      ],
      layoutAnimation: true,
      sidePanelWidth: 380,
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  computed: {
    // slider position 0..100 <-> weight 1..MAX_WEIGHT on a log scale
    minWeightPos: {
      get() {
        const w = Math.min(this.MAX_WEIGHT, Math.max(1, Number(this.minWeightDraft) || 1));
        return Math.round(100 * Math.log(w) / Math.log(this.MAX_WEIGHT));
      },
      set(pos) {
        this.minWeightDraft = Math.max(1, Math.round(Math.exp((pos / 100) * Math.log(this.MAX_WEIGHT))));
        this.applyMinWeightLater();
      },
    },
    ...mapGetters(['isAdmin']),
    isNetworkMode() { return !!this.networkId; },
    isAmongMode() { return Array.isArray(this.sourceIds); },
    centerNode() { return this.graph.nodes.find(n => n.is_center) || null; },
    headerTitle() {
      if (this.isAmongMode) return 'Распространение: связи между источниками';
      if (!this.centerUsername) return this.isNetworkMode ? 'Сетка' : 'Граф связей';
      const t = this.centerNode?.title;
      return t ? `${t} (@${this.centerUsername})` : `@${this.centerUsername}`;
    },
    tableHeight() { return `calc(${this.height} - 110px)`; },
    nodeByUsername() {
      const m = {};
      this.graph.nodes.forEach(n => { m[n.username] = n; });
      return m;
    },
    // Узлы без единой связи после фильтров (тип / мин. постов / период) не рисуем —
    // иначе при поднятом пороге каналы «висят» без стрелок. Центр остаётся всегда.
    visibleNodes() {
      return this.graph.nodes.filter(n => n.is_center || n.degree > 0);
    },
    hiddenIsolatedCount() {
      return this.graph.nodes.length - this.visibleNodes.length;
    },
    panelNode() {
      return (this.selectedUsername && this.nodeByUsername[this.selectedUsername])
        || this.nodeByUsername[this.centerUsername]
        || null;
    },
    chartOption() {
      const sizeOf = (n) => (this.nodeValues ? (this.nodeValues[n.source_id] || 0) : (n.subscribers || 0));
      const maxSubs = Math.max(1, ...this.visibleNodes.map(sizeOf));
      const nodes = this.visibleNodes.map(n => {
        const subs = sizeOf(n);
        const size = 18 + 42 * (Math.log10(subs + 1) / Math.log10(maxSubs + 1));
        const img = this.avatarSrc(n) || DEFAULT_AVATAR;
        return {
          id: n.username,
          name: (n.title || ('@' + n.username)) + (n.is_aggregator ? ' ⧉' : ''),
          value: subs,
          symbolSize: n.is_center ? Math.max(size, 48) : size,
          symbol: `image://${img}`,
          itemStyle: {
            color: n.is_center ? '#e53935' : (n.source_id ? '#5c6bc0' : '#bdbdbd'),
            borderColor: n.is_center ? '#e53935' : '#fff',
            borderWidth: n.is_center ? 4 : 1,
          },
          label: { show: n.is_center || n.degree >= 2 || this.visibleNodes.length <= 40 },
          _node: n,
        };
      });
      const maxW = Math.max(1, ...this.graph.edges.map(e => e.weight));
      const links = this.graph.edges.map(e => ({
        source: e.src,
        target: e.dst,
        value: e.weight,
        lineStyle: {
          // тонкие линии: толстая линия + маленькая стрелка сливались в «кляксу»
          width: 0.8 + 2.2 * (Math.log(e.weight + 1) / Math.log(maxW + 1)),
          color: this.typeMeta(e.edge_type).hex,
          curveness: e.edge_type === 'coposting' ? 0 : 0.15,
          opacity: 0.7,
        },
        symbol: e.edge_type === 'coposting' ? ['none', 'none'] : ['none', 'arrow'],
        symbolSize: [6, 10],
        _edge: e,
      }));
      return {
        tooltip: {
          formatter: (p) => {
            if (p.dataType === 'edge') {
              const e = p.data._edge;
              return `@${e.src} → @${e.dst}<br/>${this.typeMeta(e.edge_type).label}: <b>${e.weight}</b> постов<br/>${this.formatDate(e.first_seen_at)} — ${this.formatDate(e.last_seen_at)}`;
            }
            const n = p.data._node;
            const extra = this.nodeValues ? `<br/>сообщений: <b>${this.formatNumber(this.nodeValues[n.source_id] || 0)}</b>` : '';
            const agg = n.is_aggregator ? '<br/><b>агрегатор</b> — не участвует в кластеризации' : '';
            return `<b>${n.title || '@' + n.username}</b><br/>@${n.username}${n.subscribers ? '<br/>' + this.formatNumber(n.subscribers) + ' подписчиков' : ''}${extra}${n.network_name ? '<br/>' + n.network_name : ''}${agg}<br/>связей: ${n.degree}`;
          },
        },
        series: [{
          type: 'graph',
          layout: 'force',
          roam: true,
          draggable: true,
          data: nodes,
          links,
          edgeSymbolSize: 8,
          force: { repulsion: 260, edgeLength: [60, 180], gravity: 0.08, friction: 0.2, layoutAnimation: this.layoutAnimation },
          label: { position: 'bottom', fontSize: 11, color: '#333' },
          emphasis: { focus: 'adjacency', lineStyle: { width: 3.5 } },
          lineStyle: { color: 'source' },
        }],
      };
    },
  },
  watch: {
    sourceId() { this.init(); },
    networkId() { this.init(); },
    sourceIds() { this.init(); },
    highlightUsername() { this.applyHighlight(); },
    depth() { this.load(); this.saveSettings(); },
    periodDays() { this.load(); this.loadEdges(); this.saveSettings(); },
    layoutAnimation() { this.saveSettings(); },
    types() { this.load(); this.loadEdges(); this.saveSettings(); },
    minWeight() { this.load(); this.loadEdges(); this.saveSettings(); },
  },
  created() {
    const saved = readSettingsCookie();
    if (saved) {
      if (Array.isArray(saved.types)) this.types = saved.types;
      if (Number.isFinite(saved.minWeight)) this.minWeight = Math.min(500, Math.max(1, saved.minWeight));
      this.minWeightDraft = this.minWeight;
      if (Number.isFinite(saved.periodDays)) this.periodDays = saved.periodDays;
      if ([1, 2].includes(saved.depth)) this.depth = saved.depth;
      if (typeof saved.layoutAnimation === 'boolean') { this.layoutAnimation = saved.layoutAnimation; this._animationExplicit = true; }
      if (Number.isFinite(saved.sidePanelWidth)) this.sidePanelWidth = Math.min(900, Math.max(240, saved.sidePanelWidth));
    }
  },
  beforeUnmount() { this.stopResize(); },
  mounted() { this.init(); },
  methods: {
    saveSettings() {
      writeSettingsCookie({
        types: this.types, minWeight: this.minWeight, periodDays: this.periodDays, depth: this.depth,
        layoutAnimation: this.layoutAnimation, sidePanelWidth: this.sidePanelWidth,
      });
    },
    // Перетаскивание разделителя между графом и правой панелью
    startResize(e) {
      this._resizeStartX = e.clientX;
      this._resizeStartW = this.sidePanelWidth;
      this._onMove = (ev) => {
        const w = this._resizeStartW - (ev.clientX - this._resizeStartX);
        this.sidePanelWidth = Math.min(900, Math.max(240, w));
      };
      this._onUp = () => { this.stopResize(); this.saveSettings(); };
      window.addEventListener('mousemove', this._onMove);
      window.addEventListener('mouseup', this._onUp);
      document.body.style.userSelect = 'none';
    },
    stopResize() {
      if (this._onMove) window.removeEventListener('mousemove', this._onMove);
      if (this._onUp) window.removeEventListener('mouseup', this._onUp);
      this._onMove = null; this._onUp = null;
      document.body.style.userSelect = '';
    },
    notify(text, color = 'success') { this.snackbar = { show: true, text, color }; },
    // Масштаб кнопками — то же, что колесо мыши (roam), относительно центра холста
    zoomBy(factor) {
      const chart = this.$refs.chart;
      if (!chart || !chart.dispatchAction) return;
      const w = chart.getWidth ? chart.getWidth() : 0;
      const h = chart.getHeight ? chart.getHeight() : 0;
      chart.dispatchAction({ type: 'graphRoam', seriesIndex: 0, zoom: factor, originX: w / 2, originY: h / 2 });
    },
    applyHighlight() {
      const u = (this.highlightUsername || '').replace(/^@/, '').toLowerCase();
      if (!u || !this.nodeByUsername[u]) return;
      this.selectedUsername = u;
      this.loadEdges();
      this.$nextTick(() => {
        const chart = this.$refs.chart;
        const idx = this.visibleNodes.findIndex(n => n.username === u);
        if (chart && chart.dispatchAction && idx >= 0) {
          chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
          chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: idx });
        }
      });
    },
    canRecenter(node) {
      return !!(node && node.source_id && (this.isNetworkMode || node.source_id !== this.centerSourceId));
    },
    // «Название (@username)» для заголовков модалки примеров
    channelLabel(username, title) {
      const t = title || (this.nodeByUsername[username] || {}).title;
      return t ? `${t} (@${username})` : `@${username}`;
    },
    // Обновить флаги канала (в узлах графа и в строках панели) после действия без перезапроса
    markChannel(username, patch) {
      this.graph.nodes.forEach(n => { if (n.username === username) Object.assign(n, patch); });
      this.edgeRows.forEach(r => { if (r.username === username) Object.assign(r, patch); });
    },
    openInCatalog(row) {
      const url = this.$router.resolve({ path: '/admin/telegram', query: { username: row.username } }).href;
      window.open(url, '_blank');
    },
    async addToMySources(row) {
      try {
        await apiClient.post('/user-sources', { platform_id: 7, source_type: 'account', identifier: row.username });
        this.notify(`@${row.username} добавлен в мои источники`);
        this.markChannel(row.username, { in_my_sources: true, ...(this.isAdmin ? {} : { is_monitoring: true }) });
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.data?.message;
        this.notify(typeof msg === 'string' ? msg : 'Не удалось добавить источник', 'error');
      }
    },
    async toggleAggregator(row) {
      const next = !row.is_aggregator;
      try {
        await apiClient.put(`/sources/${row.source_id}/aggregator`, { is_aggregator: next });
        this.markChannel(row.username, { is_aggregator: next });
        this.notify(next ? `@${row.username} помечен как агрегатор — вступит в силу при следующем пересчёте сетей` : `Флаг «агрегатор» снят с @${row.username}`);
      } catch (e) {
        const msg = e?.response?.data?.detail;
        this.notify(typeof msg === 'string' ? msg : 'Не удалось изменить флаг', 'error');
      }
    },
    async startMonitoring(row) {
      try {
        await apiClient.post(`/telegram/channel/${row.username}/start`);
        this.notify(`Мониторинг @${row.username} запущен`);
        this.markChannel(row.username, { is_monitoring: true });
      } catch (e) {
        const msg = e?.response?.data?.detail;
        this.notify(typeof msg === 'string' ? msg : 'Не удалось запустить мониторинг', 'error');
      }
    },
    typeMeta(t) { return EDGE_TYPES.find(x => x.value === t) || { label: t, color: 'grey', hex: '#9e9e9e' }; },
    formatNumber(n) { return new Intl.NumberFormat('ru-RU').format(n || 0); },
    formatDate(d) { return d ? new Date(d).toLocaleDateString('ru-RU') : '—'; },
    avatarSrc(n) {
      if (!n) return '';
      // наша копия (/api/sources/{id}/avatar) и base64 из базы надёжнее внешних
      // cdn.telesco.pe-ссылок (они протухают)
      if (isStoredAvatar(n.thumbnail_url) && !this.brokenImages[n.thumbnail_url]) {
        this.preloadImage(n.thumbnail_url);
        return n.thumbnail_url;
      }
      if (n.thumbnail_encoded) return base64Src(n.thumbnail_encoded);
      if (n.thumbnail_url && !this.brokenImages[n.thumbnail_url]) {
        this.preloadImage(n.thumbnail_url);
        return n.thumbnail_url;
      }
      return '';
    },
    applyMinWeightLater() {
      clearTimeout(this.minWeightTimer);
      this.minWeightTimer = setTimeout(() => {
        const w = Math.min(this.MAX_WEIGHT, Math.max(1, Math.round(Number(this.minWeightDraft) || 1)));
        this.minWeightDraft = w;
        if (w !== this.minWeight) this.minWeight = w;
      }, 400);
    },
    preloadImage(url) {
      if (this._preloaded && this._preloaded[url]) return;
      this._preloaded = this._preloaded || {};
      this._preloaded[url] = true;
      const im = new Image();
      im.onerror = () => { this.brokenImages = { ...this.brokenImages, [url]: true }; };
      im.src = url;
    },
    init() {
      this.centerSourceId = this.sourceId;
      this.centerUsername = (this.username || '').replace(/^@/, '').toLowerCase();
      this.selectedUsername = null;
      this.networkName = '';
      this.load();
      this.loadEdges();
    },
    queryParams() {
      const p = { min_weight: this.minWeight };
      if (this.types.length && this.types.length < EDGE_TYPES.length) p.types = this.types.join(',');
      if (this.periodDays > 0) p.from = new Date(Date.now() - this.periodDays * 86400000).toISOString();
      return p;
    },
    async load() {
      if (this.isAmongMode) {
        if (!this.sourceIds.length) { this.graph = { nodes: [], edges: [], truncated: false }; return; }
        this.loading = true;
        try {
          const qp = this.queryParams();
          const { data } = await apiClient.post('/sources/graph', {
            source_ids: this.sourceIds, types: qp.types || null, min_weight: qp.min_weight, date_from: qp.from || null,
          });
          this.graph = data;
          if (!this._animationExplicit) this.layoutAnimation = (data.nodes || []).length <= 150;
        } catch (e) {
          console.error('graph-among load error', e);
          this.graph = { nodes: [], edges: [], truncated: false };
        } finally { this.loading = false; }
        return;
      }
      if (!this.isNetworkMode && !this.centerSourceId) return;
      this.loading = true;
      try {
        const url = this.isNetworkMode ? `/tg-networks/${this.networkId}/graph` : `/sources/${this.centerSourceId}/ego-graph`;
        const params = this.isNetworkMode ? this.queryParams() : { depth: this.depth, ...this.queryParams() };
        const { data } = await apiClient.get(url, { params });
        this.graph = data;
        // большие графы: без анимации, иначе force-раскладка «кружит» минутами
        if (!this._animationExplicit) this.layoutAnimation = (data.nodes || []).length <= 150;
        const center = data.nodes.find(n => n.is_center);
        if (center) {
          this.centerUsername = center.username;
          if (this.isNetworkMode) {
            this.centerSourceId = center.source_id;
            this.networkName = center.network_name || '';
            // в режиме сети центр (хаб) известен только после загрузки графа —
            // таблицу связей для него грузим здесь, а не в init()
            this.loadEdges();
          }
        }
        if (this.highlightUsername) {
          this.$nextTick(() => setTimeout(() => this.applyHighlight(), 300));
        }
      } catch (e) {
        console.error('ego-graph load error', e);
        this.graph = { nodes: [], edges: [], truncated: false };
      } finally { this.loading = false; }
    },
    async loadEdges(more = false) {
      if (this.hideSidePanel) return;
      const node = this.panelNode;
      const sid = node?.source_id || this.centerSourceId;
      if (!sid) { this.edgeRows = []; this.edgesTotal = 0; return; }
      this.edgesLoading = true;
      try {
        this.edgesPage = more ? this.edgesPage + 1 : 1;
        const { data } = await apiClient.get(`/sources/${sid}/edges`, { params: { direction: 'both', page: this.edgesPage, page_size: 50, ...this.queryParams() } });
        this.edgeRows = more ? this.edgeRows.concat(data.items) : data.items;
        this.edgesTotal = data.total;
      } catch (e) {
        console.error('edges load error', e);
      } finally { this.edgesLoading = false; }
    },
    onChartClick(p) {
      if (p.dataType === 'node') {
        this.selectedUsername = p.data._node.username;
        // подсветка через action, а не через опции: setOption пересчитывал бы force-раскладку
        // и сбрасывал зум/позиции при каждом клике
        const chart = this.$refs.chart;
        if (chart && chart.dispatchAction) {
          chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
          chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: p.dataIndex });
        }
        this.loadEdges();
      } else if (p.dataType === 'edge') {
        const e = p.data._edge;
        const src = this.nodeByUsername[e.src];
        this.openExamples({ direction: 'out', username: e.dst, edge_type: e.edge_type, _srcSourceId: src?.source_id, _srcUsername: e.src, title: (this.nodeByUsername[e.dst] || {}).title });
      }
    },
    recenter(node) {
      this.$emit('recenter', node);
      if (this.isNetworkMode) return; // страница сетки сама решает, куда вести
      this.centerSourceId = node.source_id;
      this.centerUsername = node.username;
      this.selectedUsername = null;
      this.load();
      this.loadEdges();
    },
    openExamples(row) {
      // Посты хранятся у канала-источника ребра: для исходящей связи это
      // текущий (панельный) канал, для входящей — второй конец.
      const panel = this.panelNode;
      const outSrcId = row._srcSourceId || panel?.source_id || this.centerSourceId;
      const outSrcName = row._srcUsername || panel?.username || this.centerUsername;
      const isOut = row.direction === 'out';
      const sid = isOut ? outSrcId : row.source_id;
      if (!sid) return;
      const srcLabel = this.channelLabel(outSrcName, panel?.username === outSrcName ? panel?.title : undefined);
      const otherLabel = this.channelLabel(row.username, row.title);
      this.examples = {
        sourceId: sid,
        title: isOut ? `${srcLabel} → ${otherLabel} (${this.typeMeta(row.edge_type).label})` : `${otherLabel} → ${srcLabel} (${this.typeMeta(row.edge_type).label})`,
        mention: isOut ? row.username : outSrcName,
        edgeType: row.edge_type,
      };
      this.showExamples = true;
    },
  },
};
</script>

<style scoped>
.channel-graph { --graph-height: 72vh; }
.graph-header { border-bottom: 1px solid rgba(0,0,0,0.08); }
.graph-chart { width: 100%; height: 100%; min-height: 420px; }
.graph-main { min-width: 0; }
.side-panel { border-left: 1px solid rgba(0,0,0,0.08); display: flex; flex-direction: column; flex: 0 0 auto; overflow: hidden; }
.splitter { flex: 0 0 6px; cursor: col-resize; background: rgba(0,0,0,0.04); }
.splitter:hover { background: rgba(25,118,210,0.25); }
.row-actions :deep(.v-btn) { margin: 0 -2px; }
.zoom-btn { min-width: 40px; padding: 0 4px; }
.panel-name-link { cursor: pointer; color: rgb(var(--v-theme-primary)); }
.panel-name-link:hover { text-decoration: underline; }
.controls { border-bottom: 1px solid rgba(0,0,0,0.08); }
.edges-table :deep(tbody tr) { cursor: pointer; }
</style>
